'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ModalNav } from '../Header/HeaderMobile/ModalNav';
import { ServerLink } from '../helpers';
import { AnalyticsEventName } from '@/api/analytics';
import {
  ButtonBurger,
  ButtonHome,
  ButtonPromo,
  ButtonSearch,
  ButtonSports,
} from '@/components/buttons';
import { SearchModalTabBar } from '@/components/FilterGames/Search';
import { Support } from '@/components/Support';
import { UserSessionTimer } from '@/components/UserSessionTimer';
import { generateLink } from '@/helpers/common';
import { getSportsHref } from '@/helpers/variables';
import { useAnalytics } from '@/hooks/analytics';
import { useBodyBottomPadding } from '@/hooks/body';
import useEstonia from '@/hooks/estonia';
import useEventListener from '@/hooks/eventListener';
import { useCommonStore } from '@/store';

import { ScTabBar } from './styled';

enum ModalType {
  Menu = 'Menu',
  Search = 'Search',
}

type TabBarProps = {
  title: string;
  locale: string;
  hostname: string;
  links: ServerLink[];
};

export const TabBar = ({ title, locale, links }: TabBarProps) => {
  const { sendEventToAnalytics } = useAnalytics();

  const [modal, setModal] = useState<ModalType>();

  const ref = useRef<HTMLDivElement>(null);

  const pathname = usePathname();

  const { setBodyBottomPadding } = useBodyBottomPadding();

  const handleTabBarHeight = useCommonStore.use.handleTabBarHeight();

  const { isEstonia } = useEstonia();

  const isAuthenticated = useCommonStore.use.isAuthenticated();

  useEffect(() => {
    const handleScroll = () => {
      if (ref == null || ref.current == null) return;
      setBodyBottomPadding(ref.current.offsetHeight);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [setBodyBottomPadding]);

  const handleChangeModal = useCallback(
    (modalType: ModalType | undefined, cb?: () => void) => () => {
      setModal((modal) => (modalType === modal ? undefined : modalType));
      cb && cb();
    },
    []
  );

  const isActive = useCallback(
    (modalType: ModalType) => modal === modalType,
    [modal]
  );

  const controller = (modalType: ModalType, cb?: () => void) => ({
    onClick: handleChangeModal(modalType, cb),
    active: isActive(modalType),
  });

  const modalComponent = useMemo(() => {
    switch (modal) {
      case ModalType.Menu:
        return (
          <ModalNav
            tabBarTitle={title}
            locale={locale}
            links={links}
            onClose={handleChangeModal(ModalType.Menu)}
          />
        );
      case ModalType.Search:
        return (
          <SearchModalTabBar
            onClose={handleChangeModal(ModalType.Search)}
            padding="16px 0 0 0"
          />
        );
      default:
        return null;
    }
  }, [locale, modal, title, links, handleChangeModal]);

  const sportsHref = getSportsHref(window?.location?.hostname, locale);

  useEventListener('resize', () => handleTabBarHeight(ref), ref, true);

  const handleHomeClick = () => {
    sendEventToAnalytics({
      name: AnalyticsEventName.TABBAR__BUTTONS__HOME_BUTTON__CLICK,
    });
    handleChangeModal(undefined)();
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleSportsClick = async (event: React.MouseEvent) => {
    event.preventDefault();

    await sendEventToAnalytics({
      name: AnalyticsEventName.TABBAR__BUTTONS__SPORTS_BUTTON__CLICK,
    });
    if (typeof window !== 'undefined') window.location.replace(sportsHref);
  };

  useEffect(() => {
    setModal(undefined);
  }, [pathname]);

  return (
    <>
      {modalComponent}
      {/* TODO: temporary solution, menu should not be rendered in reactPortal */}
      <ModalNav
        tabBarTitle={title}
        isHidden={true}
        locale={locale}
        links={links}
        onClose={handleChangeModal(ModalType.Menu)}
      />
      <ScTabBar ref={ref} isModal={!!modal}>
        <div className="body">
          <div className="item">
            <Link
              href={generateLink('/', locale)}
              className={pathname === generateLink('/', locale) ? 'active' : ''}
              onClick={handleHomeClick}
            >
              <ButtonHome />
            </Link>
          </div>
          {!isEstonia && (
            <div className="item">
              <Link href={sportsHref} onClick={handleSportsClick}>
                <ButtonSports />
              </Link>
            </div>
          )}
          <div className="item">
            <Support
              eventName={
                AnalyticsEventName.TABBAR__BUTTONS__SUPPORT_BUTTON__CLICK
              }
            />
          </div>
          <div className="item">
            <ButtonSearch
              {...controller(ModalType.Search, () => {
                sendEventToAnalytics({
                  name: AnalyticsEventName.TABBAR__BUTTONS__SEARCH_BUTTON__CLICK,
                });
              })}
            />
          </div>
          <div className="item">
            <Link
              href={generateLink('/promotions', locale)}
              className={
                pathname === generateLink('/promotions', locale) ? 'active' : ''
              }
              onClick={handleChangeModal(undefined)}
            >
              <ButtonPromo />
            </Link>
          </div>
          <div className="item">
            <ButtonBurger {...controller(ModalType.Menu)} />
          </div>
        </div>
        {isAuthenticated && isEstonia && <UserSessionTimer isMobile />}
      </ScTabBar>
    </>
  );
};
